import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Plus, Key, Car, Dog, Baby, Smartphone, Package, Trash2, AlertTriangle, Gift, Eye, MessageCircle, Download, ToggleLeft, ToggleRight, Image, Calendar, Filter, Heart, Siren, MapPin, Phone, QrCode as QRCodeIcon, Type, Bell, Mail, ArrowRightLeft, Building2, X, Archive, LayoutGrid, List, Search, Bike, Shirt, Watch, Home as HomeIcon, Gem } from 'lucide-react';
import { useAuth, API } from '../App';
import { categoryLabel, t, allCategories } from '../i18n';
import Header from '../components/Header';
import ArchiveModal from '../components/ArchiveModal';
import StickerPickerModal from '../components/StickerPickerModal';
import ColorPicker from '../components/ColorPicker';
import TagoraSeal from '../components/TagoraSeal';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const LocationPicker = ({ position, setPosition, onAddressFound }) => {
  const map = useMapEvents({
    click(e) {
      const pos = [e.latlng.lat, e.latlng.lng];
      setPosition(pos);
      // Reverse geocode to get address
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos[0]}&lon=${pos[1]}&zoom=16&addressdetails=1`)
        .then(r => r.json())
        .then(data => { if (data.display_name && onAddressFound) onAddressFound(data.display_name); })
        .catch(() => {});
    },
  });
  
  useEffect(() => {
    if (position) map.flyTo(position, 15, { duration: 0.8 });
  }, [position, map]);
  
  return position ? <Marker position={position} /> : null;
};

const categoryIcons = {
  keys: Key,
  phone: Smartphone,
  bag: Package,
  clothing: Shirt,
  child: Baby,
  pet: Dog,
  bike: Bike,
  car: Car,
  valuables: Gem,
  accessories: Watch,
  home: HomeIcon,
  // Backwards compatibility
  doudou: Heart,
  other: Package,
  wallet: Gem,
};

// Ordered list of categories for the create/edit form (single source of truth)
const PINGME_CATEGORIES = [
  { value: 'keys', label: 'Cles', icon: Key },
  { value: 'phone', label: 'Telephone & electronique', icon: Smartphone },
  { value: 'bag', label: 'Sac & bagage', icon: Package },
  { value: 'clothing', label: 'Vetements', icon: Shirt },
  { value: 'child', label: 'Objets enfants & doudou', icon: Baby },
  { value: 'pet', label: 'Animaux', icon: Dog },
  { value: 'bike', label: 'Velo & mobilite', icon: Bike },
  { value: 'car', label: 'Voiture', icon: Car },
  { value: 'valuables', label: 'Objets de valeur', icon: Gem },
  { value: 'accessories', label: 'Accessoires du quotidien', icon: Watch },
  { value: 'home', label: 'Maison & loisirs', icon: HomeIcon },
];



const Dashboard = () => {
  const { user, features } = useAuth();
  const navigate = useNavigate();
  const [dashTab, setDashTab] = useState('pingmes');
  const [pingmes, setPingmes] = useState([]);
  const [filteredPingmes, setFilteredPingmes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [qrZoomFor, setQrZoomFor] = useState(null); // pingme being zoomed in
  const [categories, setCategories] = useState([]);
  const [newPingme, setNewPingme] = useState({
    name: '',
    category: 'keys',
    description: '',
    reward_amount: '',
    photo_url: '',
    active_days: '',
    label_text: 'perdu ? Scannez moi',
    preset_message: '',
    preset_message_mode: 'before',
    bg_color: '',
  });
  const [creating, setCreating] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [urgentDialog, setUrgentDialog] = useState(null); // pingme_id or null
  const [urgentForm, setUrgentForm] = useState({ object_type: '', last_location: '', reward_amount: '', description: '' });
  const [urgentSubmitting, setUrgentSubmitting] = useState(false);
  const [urgentMapPosition, setUrgentMapPosition] = useState(null);
  const [geocoding, setGeocoding] = useState(false);

  const geocodeAddress = async () => {
    if (!urgentForm.last_location.trim()) return;
    setGeocoding(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(urgentForm.last_location)}&limit=1`);
      const data = await res.json();
      if (data.length > 0) {
        const pos = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        setUrgentMapPosition(pos);
        setUrgentForm(f => ({ ...f, last_location: data[0].display_name }));
      } else {
        toast.error('Adresse non trouvee');
      }
    } catch { toast.error('Erreur de geocodage'); }
    finally { setGeocoding(false); }
  };
  const [zoomedImage, setZoomedImage] = useState(null);
  const [editingPingme, setEditingPingme] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', category: '', reward_amount: '', photo_url: '', active_days: '', label_text: '', preset_message: '', preset_message_mode: 'before', notification_prefs: null, bg_color: '' });
  const [transferData, setTransferData] = useState(null); // {token, pingme_name, expires_at}
  const [editSaving, setEditSaving] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [stickerPickerFor, setStickerPickerFor] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('dashboard_view') || 'grid');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    localStorage.setItem('dashboard_view', viewMode);
  }, [viewMode]);

  useEffect(() => {
    fetchPingmes();
    fetchUnreadCount();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredPingmes(pingmes);
    } else {
      setFilteredPingmes(pingmes.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory, pingmes]);

  const fetchPingmes = async () => {
    try {
      const response = await axios.get(`${API}/pingmes`, { withCredentials: true });
      setPingmes(response.data);
      setFilteredPingmes(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de vos PingMe');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/pingmes/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread/count`, { withCredentials: true });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const createPingme = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const data = {
        ...newPingme,
        reward_amount: newPingme.reward_amount ? parseFloat(newPingme.reward_amount) : null,
        active_days: newPingme.active_days ? parseInt(newPingme.active_days) : null,
        photo_url: newPingme.photo_url || null,
        label_text: newPingme.label_text || 'perdu ? Scannez moi',
        preset_message: newPingme.preset_message || null,
        preset_message_mode: newPingme.preset_message_mode || 'before',
        bg_color: newPingme.bg_color || null,
      };
      const response = await axios.post(`${API}/pingmes`, data, { withCredentials: true });
      setPingmes([...pingmes, response.data]);
      setShowCreateDialog(false);
      setNewPingme({ name: '', category: 'keys', description: '', reward_amount: '', photo_url: '', active_days: '', label_text: 'perdu ? Scannez moi', preset_message: '', preset_message_mode: 'before', bg_color: '' });
      fetchCategories();
      toast.success('PingMe créé avec succès!');
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const toggleLostMode = async (pingme) => {
    if (pingme.is_lost) {
      // Cancel lost mode
      try {
        const response = await axios.post(
          `${API}/pingmes/${pingme.pingme_id}/cancel-urgent`,
          {},
          { withCredentials: true }
        );
        setPingmes(pingmes.map(p => p.pingme_id === pingme.pingme_id ? response.data : p));
        toast.success('Mode Perdu desactive');
      } catch (error) {
        toast.error('Erreur lors de la mise a jour');
      }
    } else {
      // Open urgent lost dialog
      setUrgentDialog(pingme.pingme_id);
      setUrgentForm({ object_type: pingme.category, last_location: '', reward_amount: pingme.reward_amount || '', description: '' });
      setUrgentMapPosition(null);
    }
  };

  const submitUrgentLost = async (e) => {
    e.preventDefault();
    if (!urgentDialog) return;
    setUrgentSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/pingmes/${urgentDialog}/urgent-lost`,
        {
          ...urgentForm,
          reward_amount: urgentForm.reward_amount ? parseFloat(urgentForm.reward_amount) : null,
          latitude: urgentMapPosition ? urgentMapPosition[0] : null,
          longitude: urgentMapPosition ? urgentMapPosition[1] : null
        },
        { withCredentials: true }
      );
      setPingmes(pingmes.map(p => p.pingme_id === urgentDialog ? response.data : p));
      toast.success('Objet declare perdu (urgent) ! Vous recevrez des alertes si quelqu\'un le scanne.');
      setUrgentDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setUrgentSubmitting(false);
    }
  };

  const toggleActive = async (pingme) => {
    try {
      const response = await axios.patch(
        `${API}/pingmes/${pingme.pingme_id}`,
        { is_active: !pingme.is_active },
        { withCredentials: true }
      );
      setPingmes(pingmes.map(p => p.pingme_id === pingme.pingme_id ? response.data : p));
      toast.success(response.data.is_active ? 'PingMe activé' : 'PingMe désactivé');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deletePingme = async (pingmeId) => {
    if (!window.confirm('Archiver ce PingMe ? Il sera recuperable pendant 30 jours depuis votre archive.')) return;
    try {
      await axios.delete(`${API}/pingmes/${pingmeId}`, { withCredentials: true });
      setPingmes(pingmes.filter(p => p.pingme_id !== pingmeId));
      fetchCategories();
      toast.success('PingMe archive (recuperable 30 jours)');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEdit = (pingme) => {
    setEditForm({
      name: pingme.name || '',
      description: pingme.description || '',
      category: pingme.category || 'other',
      reward_amount: pingme.reward_amount || '',
      photo_url: pingme.photo_url || '',
      active_days: pingme.active_days || '',
      label_text: pingme.label_text || 'perdu ? Scannez moi',
      preset_message: pingme.preset_message || '',
      preset_message_mode: pingme.preset_message_mode || 'before',
      notification_prefs: pingme.notification_prefs || null,
      bg_color: pingme.bg_color || ''
    });
    setEditingPingme(pingme.pingme_id);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const payload = {};
      if (editForm.name) payload.name = editForm.name;
      if (editForm.category) payload.category = editForm.category;
      if (editForm.photo_url !== undefined) payload.photo_url = editForm.photo_url;
      if (editForm.label_text !== undefined) payload.label_text = editForm.label_text || 'perdu ? Scannez moi';
      if (editForm.preset_message !== undefined) payload.preset_message = editForm.preset_message || null;
      if (editForm.preset_message_mode) payload.preset_message_mode = editForm.preset_message_mode;
      payload.notification_prefs = editForm.notification_prefs;
      payload.bg_color = editForm.bg_color || null;

      const response = await axios.patch(
        `${API}/pingmes/${editingPingme}`,
        payload,
        { withCredentials: true }
      );
      setPingmes(pingmes.map(p => p.pingme_id === editingPingme ? response.data : p));
      toast.success('PingMe mis a jour !');
      setEditingPingme(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditPhotoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image trop volumineuse (max 5 Mo)'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      setEditForm({ ...editForm, photo_url: res.data.url });
      toast.success('Image uploadee');
    } catch { toast.error('Erreur upload'); }
  };

  const resolvePhotoUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${API.replace('/api', '')}${url}` : url;
  };

  const downloadQR = (pingme) => {
    const svg = document.getElementById(`qr-${pingme.pingme_id}`);
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    const labelText = pingme.label_text || 'perdu ? Scannez moi';
    
    img.onload = () => {
      const textHeight = 44;
      canvas.width = img.width;
      canvas.height = img.height + textHeight;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      // Draw label text below QR in black, wrap if needed
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.textAlign = 'center';
      const maxWidth = canvas.width - 20;
      const words = labelText.split(' ');
      let line = '';
      let y = img.height + 16;
      for (const word of words) {
        const test = line + (line ? ' ' : '') + word;
        if (ctx.measureText(test).width > maxWidth && line) {
          ctx.fillText(line, canvas.width / 2, y);
          line = word;
          y += 18;
        } else { line = test; }
      }
      ctx.fillText(line, canvas.width / 2, y);
      
      const link = document.createElement('a');
      link.download = `pingme-${pingme.name}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const formatRemainingDays = (activeUntil) => {
    if (!activeUntil) return null;
    const until = new Date(activeUntil);
    const now = new Date();
    const days = Math.ceil((until - now) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Expiré';
    return `${days}j restants`;
  };

  const lostPingmes = pingmes.filter(p => p.is_lost);

  const generateTransfer = async (pingme) => {
    try {
      const res = await axios.post(`${API}/pingmes/${pingme.pingme_id}/transfer/generate`, {}, { withCredentials: true });
      const baseUrl = window.location.origin;
      setTransferData({
        ...res.data,
        transferUrl: `${baseUrl}/transfer/${res.data.transfer_token}`,
        pingme_name: pingme.name,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Lost Mode Banner */}
      {lostPingmes.length > 0 && (
        <div className="bg-danger text-white border-b-2 border-charcoal px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <AlertTriangle className="w-6 h-6" strokeWidth={2.5} />
            <span className="font-bold">
              {lostPingmes.length} objet{lostPingmes.length > 1 ? 's' : ''} en mode "Perdu"
            </span>
          </div>
        </div>
      )}

      <main className="px-6 sm:px-8 lg:px-12 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-charcoal font-heading">
                Bonjour, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-charcoal/70 mt-1">Gerez vos PingMe et restez connecte</p>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <Link
                to="/messages"
                data-testid="messages-link"
                className="btn-outline relative flex items-center gap-2 px-3 sm:px-5"
                title="Messages"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                <span className="hidden sm:inline">Messages</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-danger text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-charcoal">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <button
                onClick={() => setShowArchive(true)}
                data-testid="archive-open-btn"
                className="btn-outline flex items-center gap-2 px-3 sm:px-5"
                title="Archive"
              >
                <Archive className="w-5 h-5" strokeWidth={2.5} />
                <span className="hidden sm:inline">Archive</span>
              </button>
              {dashTab === 'pingmes' && (
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <button data-testid="create-pingme-btn" className="btn-primary flex items-center gap-2 px-4 sm:px-5 whitespace-nowrap">
                    <Plus className="w-5 h-5" strokeWidth={2.5} />
                    <span className="hidden sm:inline">Nouveau PingMe</span>
                    <span className="sm:hidden">Nouveau</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold font-heading">Créer un PingMe</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createPingme} className="space-y-5 mt-4">
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                        Nom de l'objet *
                      </label>
                      <input
                        type="text"
                        value={newPingme.name}
                        onChange={(e) => setNewPingme({ ...newPingme, name: e.target.value })}
                        data-testid="pingme-name-input"
                        className="input-brutal w-full"
                        placeholder={t('dashboard.name')}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                        Catégorie
                      </label>
                      <select
                        value={newPingme.category}
                        onChange={(e) => setNewPingme({ ...newPingme, category: e.target.value })}
                        data-testid="pingme-category-select"
                        className="input-brutal w-full"
                      >
                        {PINGME_CATEGORIES.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                        Photo (optionnel)
                      </label>
                      <div className="flex gap-2 items-start">
                        <label className="input-brutal flex-1 cursor-pointer flex items-center gap-2 text-charcoal/50" data-testid="pingme-photo-upload">
                          <Image className="w-5 h-5" />
                          <span className="text-sm truncate">{newPingme.photo_url ? 'Image selectionnee' : 'Choisir une image...'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) { toast.error('Image trop volumineuse (max 5 Mo)'); return; }
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const res = await axios.post(`${API}/upload/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
                                setNewPingme({ ...newPingme, photo_url: res.data.url });
                                toast.success('Image uploadee');
                              } catch { toast.error('Erreur upload'); }
                            }}
                          />
                        </label>
                        <div className="w-12 h-12 rounded-lg border-2 border-charcoal bg-surface-alt flex items-center justify-center overflow-hidden shrink-0">
                          {newPingme.photo_url ? (
                            <img src={newPingme.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${newPingme.photo_url}` : newPingme.photo_url} alt="Preview" className="w-full h-full object-cover" />
                          ) : (
                            <Image className="w-6 h-6 text-charcoal/40" />
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                        Texte de l'etiquette
                      </label>
                      <input
                        type="text"
                        value={newPingme.label_text}
                        onChange={(e) => setNewPingme({ ...newPingme, label_text: e.target.value })}
                        data-testid="pingme-label-text-input"
                        className="input-brutal w-full"
                        placeholder={t('dashboard.labelText')}
                      />
                      <p className="text-xs text-charcoal/50 mt-1">Texte affiche sur la plaquette/sticker</p>
                    </div>
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                        Message pre-enregistre (optionnel)
                      </label>
                      <textarea
                        value={newPingme.preset_message}
                        onChange={(e) => setNewPingme({ ...newPingme, preset_message: e.target.value })}
                        data-testid="pingme-preset-message-input"
                        className="input-brutal w-full h-20 resize-none"
                        placeholder={t('dashboard.presetMessage')}
                      />
                      {newPingme.preset_message && (
                        <div className="mt-2">
                          <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Mode d'affichage</label>
                          <select
                            value={newPingme.preset_message_mode}
                            onChange={(e) => setNewPingme({ ...newPingme, preset_message_mode: e.target.value })}
                            className="input-brutal w-full"
                            data-testid="pingme-preset-mode-select"
                          >
                            <option value="before">{t('dashboard.presetModeBefore')}</option>
                            <option value="only">Afficher seul (pas de conversation)</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Color picker */}
                    <ColorPicker
                      label="Couleur du sticker"
                      value={newPingme.bg_color}
                      onChange={(c) => setNewPingme({ ...newPingme, bg_color: c })}
                      testid="create-color-picker"
                    />

                    <button
                      type="submit"
                      disabled={creating}
                      data-testid="create-pingme-submit"
                      className="w-full btn-primary disabled:opacity-50"
                    >
                      {creating ? 'Création...' : 'Créer le PingMe'}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
              )}
            </div>
          </div>

          {/* Tab Navigation removed - organizations are now managed via API by admin */}

          {dashTab === 'pingmes' && (
          <>
          {/* Urgent Lost Dialog */}
          {urgentDialog && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/50 backdrop-blur-sm px-4">
              <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" data-testid="urgent-lost-dialog">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-danger w-12 h-12 rounded-full border-2 border-charcoal flex items-center justify-center">
                    <Siren className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg">Declarer perdu (urgent)</h3>
                    <p className="text-xs text-charcoal/60">Les scanners verront un signal urgent</p>
                  </div>
                </div>
                <form onSubmit={submitUrgentLost} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Type d'objet *</label>
                    <select
                      className="input-brutal w-full"
                      value={urgentForm.object_type}
                      onChange={(e) => setUrgentForm({ ...urgentForm, object_type: e.target.value })}
                      required
                      data-testid="urgent-object-type"
                    >
                      <option value="">{t('actions.search')}</option>
                      <option value="keys">Cles</option>
                      <option value="phone">Telephone</option>
                      <option value="bag">Sac</option>
                      <option value="wallet">Portefeuille</option>
                      <option value="pet">Animal</option>
                      <option value="child">Enfant / Doudou</option>
                      <option value="car">Vehicule</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Derniere localisation approximative</label>
                    <div className="flex gap-2">
                      <input
                        className="input-brutal flex-1"
                        placeholder={t('dashboard.lastLocation')}
                        value={urgentForm.last_location}
                        onChange={(e) => setUrgentForm({ ...urgentForm, last_location: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); geocodeAddress(); } }}
                        data-testid="urgent-location"
                      />
                      <button
                        type="button"
                        onClick={geocodeAddress}
                        disabled={geocoding || !urgentForm.last_location.trim()}
                        className="btn-secondary px-4 disabled:opacity-50 flex items-center gap-1"
                        data-testid="urgent-geocode-btn"
                      >
                        <MapPin className="w-4 h-4" />
                        {geocoding ? '...' : 'Chercher'}
                      </button>
                    </div>
                    <div className="mt-2 border-2 border-charcoal rounded-xl overflow-hidden" style={{ height: '220px' }} data-testid="urgent-map">
                      <MapContainer
                        center={urgentMapPosition || [46.603354, 1.888334]}
                        zoom={urgentMapPosition ? 15 : 5}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <LocationPicker
                          position={urgentMapPosition}
                          setPosition={setUrgentMapPosition}
                          onAddressFound={(addr) => setUrgentForm(f => ({ ...f, last_location: addr }))}
                        />
                      </MapContainer>
                    </div>
                    <p className="text-xs text-charcoal/50 mt-1">
                      {urgentMapPosition
                        ? `Position : ${urgentMapPosition[0].toFixed(4)}, ${urgentMapPosition[1].toFixed(4)}`
                        : 'Tapez une adresse et cliquez Chercher, ou cliquez directement sur la carte'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Recompense (EUR)</label>
                    <input
                      className="input-brutal w-full"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t('dashboard.rewardAmount')}
                      value={urgentForm.reward_amount}
                      onChange={(e) => setUrgentForm({ ...urgentForm, reward_amount: e.target.value })}
                      data-testid="urgent-reward"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Details supplementaires</label>
                    <textarea
                      className="input-brutal w-full h-16 resize-none"
                      placeholder={t('dashboard.urgentDesc')}
                      value={urgentForm.description}
                      onChange={(e) => setUrgentForm({ ...urgentForm, description: e.target.value })}
                      data-testid="urgent-description"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button type="submit" disabled={urgentSubmitting} className="flex-1 bg-danger text-white font-bold py-3 rounded-full border-2 border-charcoal shadow-brutal hover:shadow-brutal-lg transition-all disabled:opacity-50" data-testid="urgent-submit-btn">
                      {urgentSubmitting ? 'Envoi...' : 'Declarer perdu'}
                    </button>
                    <button type="button" onClick={() => setUrgentDialog(null)} className="btn-outline py-3 px-6">Annuler</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Stats - compact clickable chips */}
          {(() => {
            const totalScans = pingmes.reduce((acc, p) => acc + (p.scan_count || 0), 0);
            const totalConvs = pingmes.reduce((acc, p) => acc + (p.conversation_count || 0), 0);
            const linkedToOrg = pingmes.filter(p => p.organization).length;
            const archivedCount = pingmes.filter(p => p.deleted_at).length;
            const handleSearch = (q) => setSearchQuery(q || '');
            const stats = [
              { id: 'pingme', label: 'PingMe', value: pingmes.length, sub: `${pingmes.filter(p => p.is_active).length} actifs`, icon: QRCodeIcon, color: 'text-charcoal', bg: 'bg-primary/15', onClick: () => { setSelectedCategory('all'); handleSearch(''); } },
              { id: 'scans', label: 'Scans', value: totalScans, sub: pingmes.length ? `${(totalScans / pingmes.length).toFixed(1)} / pingme` : '0', icon: Eye, color: 'text-charcoal', bg: 'bg-secondary/30', onClick: null },
              { id: 'lost', label: 'Perdus', value: lostPingmes.length, sub: lostPingmes.length ? 'En cours' : 'Aucun', icon: Siren, color: 'text-danger', bg: 'bg-danger/10', onClick: lostPingmes.length ? () => { const el = document.getElementById('lost-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); } : null },
              { id: 'convs', label: 'Messages', value: totalConvs, sub: features?.messaging_enabled !== false ? 'Recus' : 'Off', icon: MessageCircle, color: 'text-charcoal', bg: 'bg-accent/30', onClick: features?.messaging_enabled !== false ? () => navigate('/messages') : null, link: features?.messaging_enabled !== false ? '/messages' : null },
              { id: 'orgs', label: 'Orgs', value: linkedToOrg, sub: linkedToOrg ? 'Lies' : 'Aucun', icon: Building2, color: 'text-charcoal', bg: 'bg-charcoal/10', onClick: () => navigate('/organizations'), link: '/organizations' },
              { id: 'archives', label: 'Archives', value: archivedCount, sub: archivedCount ? 'A purger' : 'Vide', icon: Archive, color: 'text-charcoal', bg: 'bg-surface-alt', onClick: () => setShowArchive(true) },
            ];
            return (
              <div className="mb-6 -mx-2 px-2 overflow-x-auto scrollbar-hide" data-testid="dashboard-stats">
                <div className="flex gap-2 min-w-max sm:grid sm:grid-cols-3 lg:grid-cols-6 sm:min-w-0">
                  {stats.map((s) => {
                    const Ic = s.icon;
                    const clickable = !!s.onClick;
                    const Cmp = s.link ? Link : 'button';
                    const props = s.link ? { to: s.link } : { type: 'button', onClick: s.onClick };
                    return (
                      <Cmp
                        key={s.id}
                        {...props}
                        disabled={!clickable && !s.link}
                        className={`shrink-0 sm:shrink min-w-[7.5rem] sm:min-w-0 border-2 border-charcoal rounded-xl p-2.5 flex items-center gap-2 text-left bg-white transition-all ${clickable || s.link ? 'cursor-pointer hover:shadow-brutal hover:-translate-y-0.5' : 'cursor-default opacity-95'}`}
                        data-testid={`dashboard-stat-${s.id}`}
                      >
                        <div className={`${s.bg} w-9 h-9 rounded-lg border border-charcoal/20 flex items-center justify-center shrink-0`}>
                          <Ic className={`w-4 h-4 ${s.color}`} strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-lg text-charcoal leading-tight">{s.value}</p>
                          <p className="text-[10px] uppercase font-bold tracking-wider text-charcoal/50 leading-tight">{s.label}</p>
                          <p className="text-[10px] text-charcoal/40 truncate">{s.sub}</p>
                        </div>
                      </Cmp>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Toolbar: filter + search + view toggle */}
          {pingmes.length > 0 && (
            <div className="mb-4 space-y-3">
              {/* Search + View toggle */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
                  <input
                    type="text"
                    placeholder="Rechercher un PingMe..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-brutal w-full pl-10 py-2 text-sm"
                    data-testid="pingme-search-input"
                  />
                </div>
                <div className="flex items-center gap-1 bg-white border-2 border-charcoal rounded-full p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-full transition-all ${viewMode === 'grid' ? 'bg-charcoal text-white' : 'text-charcoal/60 hover:text-charcoal'}`}
                    title="Vue grille"
                    data-testid="view-mode-grid"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-full transition-all ${viewMode === 'list' ? 'bg-charcoal text-white' : 'text-charcoal/60 hover:text-charcoal'}`}
                    title="Vue liste"
                    data-testid="view-mode-list"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Category Filter */}
              {categories.length > 0 && (
                <div className="flex items-center gap-3 flex-wrap">
                  <Filter className="w-4 h-4 text-charcoal/70 shrink-0" />
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`px-3 py-1 rounded-full border-2 font-bold text-xs transition-all ${
                        selectedCategory === 'all' ? 'border-charcoal bg-charcoal text-white' : 'border-charcoal/30 text-charcoal/70 hover:border-charcoal'
                      }`}
                    >
                      Tous ({pingmes.length})
                    </button>
                    {categories.map((cat) => {
                      // Resolve display label: prefer org category label, fallback to i18n, then key, then "Autre"
                      const sample = pingmes.find((p) => p.category === cat.category);
                      const orgLabel = sample?.organization?.categories?.find((c) => c.key === cat.category)?.label;
                      const display = orgLabel || categoryLabel(cat.category) || cat.category || 'Sans categorie';
                      return (
                        <button
                          key={cat.category}
                          onClick={() => setSelectedCategory(cat.category)}
                          className={`px-3 py-1 rounded-full border-2 font-bold text-xs transition-all ${
                            selectedCategory === cat.category ? 'border-charcoal bg-charcoal text-white' : 'border-charcoal/30 text-charcoal/70 hover:border-charcoal'
                          }`}
                        >
                          {display} ({cat.count})
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PingMe List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : filteredPingmes.length === 0 ? (
            <div className="card-brutal text-center py-12">
              <div className="bg-secondary w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
                <Package className="w-10 h-10 text-charcoal" strokeWidth={2.5} />
              </div>
              <h3 className="text-xl font-bold text-charcoal font-heading mb-2">
                {selectedCategory === 'all' ? 'Aucun PingMe' : `Aucun PingMe dans ${categoryLabel(selectedCategory) || selectedCategory}`}
              </h3>
              <p className="text-charcoal/70 mb-6">
                {selectedCategory === 'all' ? 'Créez votre premier PingMe pour protéger vos objets' : 'Changez de filtre ou créez un nouveau PingMe'}
              </p>
              <button
                onClick={() => setShowCreateDialog(true)}
                data-testid="empty-create-pingme-btn"
                className="btn-primary"
              >
                <Plus className="w-5 h-5 inline mr-2" strokeWidth={2.5} />
                Créer un PingMe
              </button>
            </div>
          ) : (() => {
            const q = searchQuery.trim().toLowerCase();
            const visible = q
              ? filteredPingmes.filter((p) =>
                  (p.name || '').toLowerCase().includes(q)
                  || (p.label_text || '').toLowerCase().includes(q)
                  || (categoryLabel(p.category) || '').toLowerCase().includes(q)
                  || (p.organization?.name || '').toLowerCase().includes(q)
                  || (p.organization?.display_name || '').toLowerCase().includes(q)
                )
              : filteredPingmes;
            if (visible.length === 0) {
              return (
                <div className="card-brutal text-center py-8">
                  <p className="text-sm text-charcoal/60">Aucun PingMe ne correspond a votre recherche.</p>
                </div>
              );
            }

            // Group: personal first, then one per org (sorted by org name)
            const personal = visible.filter((p) => !p.organization);
            const orgsMap = new Map();
            visible.filter((p) => p.organization).forEach((p) => {
              const oid = p.organization.org_id || p.organization.name;
              if (!orgsMap.has(oid)) orgsMap.set(oid, { org: p.organization, items: [] });
              orgsMap.get(oid).items.push(p);
            });
            const orgGroups = Array.from(orgsMap.values()).sort((a, b) =>
              (a.org.display_name || a.org.name || '').localeCompare(b.org.display_name || b.org.name || '')
            );
            const groups = [];
            if (personal.length > 0) {
              groups.push({ key: 'personal', title: 'Mes PingMe personnels', items: personal });
            }
            orgGroups.forEach((g) => {
              groups.push({
                key: `org_${g.org.org_id || g.org.name}`,
                title: g.org.display_name || g.org.name,
                org: g.org,
                items: g.items,
              });
            });

            const renderRow = (pingme) => {
                    const Icon = categoryIcons[pingme.category] || Package;
                    return (
                      <div
                        key={pingme.pingme_id}
                        data-testid={`pingme-row-${pingme.pingme_id}`}
                        className={`flex items-center gap-3 px-3 py-2 bg-white border-2 border-charcoal rounded-xl hover:bg-surface-alt cursor-pointer transition-colors ${pingme.is_lost ? 'ring-2 ring-danger' : ''} ${!pingme.is_active ? 'opacity-50' : ''}`}
                        onClick={() => openEdit(pingme)}
                      >
                        {pingme.photo_url ? (
                          <img
                            src={pingme.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${pingme.photo_url}` : pingme.photo_url}
                            alt={pingme.name}
                            className="w-9 h-9 rounded-lg border border-charcoal object-cover shrink-0"
                          />
                        ) : (
                          <div className={`${pingme.is_lost ? 'bg-danger' : 'bg-secondary'} w-9 h-9 rounded-lg border border-charcoal flex items-center justify-center shrink-0`}>
                            <Icon className="w-4 h-4 text-charcoal" strokeWidth={2.5} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-sm text-charcoal truncate">{pingme.name}</p>
                            {pingme.is_lost && <span className="bg-danger text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">PERDU</span>}
                            {!pingme.is_active && <span className="text-[9px] font-bold text-danger">OFF</span>}
                          </div>
                          <p className="text-[11px] text-charcoal/50 truncate flex items-center gap-1.5">
                            <span>{(pingme.organization?.categories?.find((c) => c.key === pingme.category)?.label) || categoryLabel(pingme.category) || 'Sans categorie'}</span>
                            <span className="text-charcoal/20">·</span>
                            <span className="inline-flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{pingme.scan_count || 0}</span>
                            {(pingme.pingme_id || '').startsWith('TGR-') && (
                              <>
                                <span className="text-charcoal/20">·</span>
                                <code className="font-mono text-[9px] tracking-wider text-charcoal/60" title="Tagora ID">{pingme.pingme_id}</code>
                              </>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => toggleLostMode(pingme)}
                            data-testid={`toggle-lost-${pingme.pingme_id}`}
                            className={`text-[10px] font-bold px-2 py-1 rounded-full border border-charcoal ${pingme.is_lost ? 'bg-danger text-white' : 'bg-white hover:bg-danger/10'}`}
                            title={pingme.is_lost ? 'Marquer comme retrouve' : 'Declarer perdu'}
                          >
                            <Siren className="w-3 h-3 inline" />
                          </button>
                          <button onClick={() => setStickerPickerFor(pingme)} data-testid={`download-qr-${pingme.pingme_id}`} disabled={!!pingme.org_id} className={`p-1.5 rounded-full border ${pingme.org_id ? 'border-charcoal/15 text-charcoal/30 cursor-not-allowed' : 'border-charcoal/30 hover:bg-surface-alt'}`} title={pingme.org_id ? 'Sticker gere par votre organisation' : 'Telecharger un sticker'}>
                            <Download className="w-3 h-3" />
                          </button>
                          {features?.messaging_enabled !== false && (
                            <Link to={`/messages/${pingme.pingme_id}`} data-testid={`view-messages-${pingme.pingme_id}`} className="p-1.5 rounded-full border border-charcoal/30 hover:bg-surface-alt" title="Messages">
                              <MessageCircle className="w-3 h-3" />
                            </Link>
                          )}
                          <button onClick={() => generateTransfer(pingme)} data-testid={`transfer-${pingme.pingme_id}`} className="p-1.5 rounded-full border border-charcoal/30 hover:bg-accent/30" title="Transferer">
                            <ArrowRightLeft className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
            };

            const renderGroupHeader = (g) => (
              <div className="flex items-center gap-2 mb-2 mt-1" data-testid={`group-header-${g.key}`}>
                {g.org ? (
                  g.org.logo_url ? (
                    <img
                      src={g.org.logo_url.startsWith('/api') ? `${API.replace('/api', '')}${g.org.logo_url}` : g.org.logo_url}
                      alt=""
                      className="w-5 h-5 rounded-full border border-charcoal/30 object-cover"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full border border-charcoal/30 bg-charcoal/90 flex items-center justify-center">
                      <Building2 className="w-3 h-3 text-white" />
                    </div>
                  )
                ) : (
                  <div className="w-5 h-5 rounded-full border border-charcoal/30 bg-secondary flex items-center justify-center">
                    <Eye className="w-3 h-3 text-charcoal" />
                  </div>
                )}
                <h3 className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal">{g.title}</h3>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-charcoal/10 text-charcoal/60">{g.items.length}</span>
                <div className="flex-1 h-px bg-charcoal/10" />
              </div>
            );

            if (viewMode === 'list') {
              return (
                <div className="space-y-5" data-testid="pingme-list-view">
                  {groups.map((g) => (
                    <div key={g.key}>
                      {renderGroupHeader(g)}
                      <div className="grid md:grid-cols-2 gap-3">
                        {g.items.map(renderRow)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            }
            const renderCard = (pingme) => {
                  const Icon = categoryIcons[pingme.category] || Package;
                  return (
                    <div
                      key={pingme.pingme_id}
                      data-testid={`pingme-card-${pingme.pingme_id}`}
                      className={`bg-white border-2 border-charcoal rounded-xl p-2 relative cursor-pointer hover:shadow-brutal hover:-translate-y-0.5 transition-all ${pingme.is_lost ? 'ring-2 ring-danger' : ''} ${!pingme.is_active ? 'opacity-50' : ''}`}
                      onClick={() => openEdit(pingme)}
                    >
                      {pingme.is_lost && (
                        <div className="absolute -top-1.5 -right-1.5 bg-danger text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-charcoal z-10">PERDU</div>
                      )}

                      {/* Hero image (photo perso > org cover > org logo > catégorie icon) */}
                      {(() => {
                        const photo = pingme.photo_url
                          ? (pingme.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${pingme.photo_url}` : pingme.photo_url)
                          : null;
                        const orgCover = pingme.organization?.cover_image_url
                          ? (pingme.organization.cover_image_url.startsWith('/api') ? `${API.replace('/api', '')}${pingme.organization.cover_image_url}` : pingme.organization.cover_image_url)
                          : null;
                        const orgLogo = pingme.organization?.logo_url
                          ? (pingme.organization.logo_url.startsWith('/api') ? `${API.replace('/api', '')}${pingme.organization.logo_url}` : pingme.organization.logo_url)
                          : null;
                        const heroImage = photo || orgCover || null;
                        // bg color for QR mini & contrast
                        const bg = pingme.bg_color || '#FFFFFF';
                        const h = bg.replace('#', '');
                        const r = parseInt(h.substring(0, 2) || '0', 16) / 255;
                        const g = parseInt(h.substring(2, 4) || '0', 16) / 255;
                        const b = parseInt(h.substring(4, 6) || '0', 16) / 255;
                        const ch = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
                        const lum = 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
                        const fg = lum < 0.45 ? '#FFFFFF' : '#0f172a';
                        const iconColor = lum < 0.45 ? 'text-white' : 'text-charcoal';
                        return (
                          <div className="relative mb-2 rounded-lg overflow-hidden border-2 border-charcoal aspect-[2/1]" style={{ background: bg }}>
                            {heroImage ? (
                              <img src={heroImage} alt={pingme.name} className="w-full h-full object-cover" />
                            ) : orgLogo ? (
                              <div className="w-full h-full flex items-center justify-center p-4">
                                <img src={orgLogo} alt={pingme.organization?.display_name || pingme.organization?.name} className="max-w-[70%] max-h-[70%] object-contain" />
                              </div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Icon className={`w-10 h-10 ${iconColor}`} strokeWidth={1.5} />
                              </div>
                            )}
                            {/* Mini QR overlay (bottom-right) - click to zoom */}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setQrZoomFor(pingme); }}
                              className="absolute bottom-1.5 right-1.5 rounded-md border-2 border-charcoal shadow-brutal-sm hover:scale-110 transition-transform p-0.5"
                              style={{ backgroundColor: bg }}
                              title="Voir le QR code"
                              data-testid={`mini-qr-${pingme.pingme_id}`}
                            >
                              <QRCodeSVG value={pingme.qr_code_url} size={32} level="L" includeMargin={false} bgColor={bg} fgColor={fg} />
                            </button>
                            {/* Org logo badge (top-left) if a custom photo is being used */}
                            {photo && orgLogo && (
                              <div className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-white border border-charcoal/30 flex items-center justify-center shadow-sm overflow-hidden" title={pingme.organization?.display_name}>
                                <img src={orgLogo} alt="" className="w-full h-full object-contain p-0.5" />
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Name + category */}
                      <div className="mb-1.5">
                        <p className="font-bold text-charcoal text-xs truncate leading-tight">{pingme.name}</p>
                        <p className="text-[9px] text-charcoal/50 truncate">{(pingme.organization?.categories?.find((c) => c.key === pingme.category)?.label) || categoryLabel(pingme.category)}</p>
                      </div>

                      {/* Stats row */}
                      <div className="flex items-center justify-between text-[9px] text-charcoal/50 mb-1">
                        <span className="flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{pingme.scan_count || 0}</span>
                        {!pingme.is_active && <span className="text-danger font-bold">OFF</span>}
                      </div>

                      {/* Tagora ID (compact) */}
                      {(pingme.pingme_id || '').startsWith('TGR-') && (
                        <Link
                          to={`/verify/${pingme.pingme_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="block mb-1.5 hover:opacity-80 transition-opacity"
                          title={`Verifier ${pingme.pingme_id}`}
                          data-testid={`tagora-id-${pingme.pingme_id}`}
                        >
                          <code className="font-mono text-[8.5px] tracking-wide text-charcoal/50 truncate block text-center bg-surface-alt rounded px-1 py-0.5 border border-charcoal/5">
                            {pingme.pingme_id}
                          </code>
                        </Link>
                      )}

                      {/* Compact actions - sticker = primary action (disabled for org-managed pingmes) */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => !pingme.org_id && setStickerPickerFor(pingme)}
                          data-testid={`download-qr-${pingme.pingme_id}`}
                          disabled={!!pingme.org_id}
                          className={`flex-1 text-[10px] font-bold px-1.5 py-1 rounded-full border border-charcoal transition-all flex items-center justify-center gap-1 ${
                            pingme.org_id
                              ? 'bg-surface-alt text-charcoal/40 cursor-not-allowed border-charcoal/20'
                              : 'bg-primary text-charcoal hover:bg-primary/80'
                          }`}
                          title={pingme.org_id ? 'Sticker gere par votre organisation' : 'Telecharger un sticker'}
                        >
                          <Download className="w-3 h-3" strokeWidth={2.5} />
                          <span>{pingme.org_id ? 'Geree' : 'Sticker'}</span>
                        </button>
                        <button
                          onClick={() => toggleLostMode(pingme)}
                          data-testid={`toggle-lost-${pingme.pingme_id}`}
                          className={`p-1.5 rounded-full border border-charcoal transition-all ${
                            pingme.is_lost ? 'bg-danger text-white' : 'bg-white hover:bg-danger/10'
                          }`}
                          title={pingme.is_lost ? 'Marquer comme retrouve' : 'Declarer perdu'}
                        >
                          <Siren className="w-3 h-3" />
                        </button>
                        {features?.messaging_enabled !== false && (
                          <Link to={`/messages/${pingme.pingme_id}`} data-testid={`view-messages-${pingme.pingme_id}`} className="p-1.5 rounded-full border border-charcoal/30 hover:bg-surface-alt" title="Messages">
                            <MessageCircle className="w-3 h-3" />
                          </Link>
                        )}
                        <button
                          onClick={() => generateTransfer(pingme)}
                          data-testid={`transfer-${pingme.pingme_id}`}
                          className="p-1.5 rounded-full border border-charcoal/30 hover:bg-accent/30"
                          title="Transferer"
                        >
                          <ArrowRightLeft className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
            };

            return (
              <div className="space-y-5" data-testid="pingme-grid-view">
                {groups.map((g) => (
                  <div key={g.key}>
                    {renderGroupHeader(g)}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
                      {g.items.map(renderCard)}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          </>
          )}
        </div>
      </main>

      {/* Edit PingMe Dialog */}
      {editingPingme && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/50 backdrop-blur-sm px-4" onClick={() => setEditingPingme(null)}>
          <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-pingme-dialog">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-bold text-lg">Modifier le PingMe</h3>
              <button onClick={() => setEditingPingme(null)} className="p-1 hover:bg-surface-alt rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            {/* Org branding preview — what finders see when scanning */}
            {(() => {
              const pm = pingmes.find((p) => p.pingme_id === editingPingme);
              const org = pm?.organization;
              if (!org) return null;
              const primary = org.primary_color || '#FF6B35';
              return (
                <div
                  className="mb-5 rounded-xl border-2 border-charcoal overflow-hidden"
                  style={{ backgroundColor: primary + '12' }}
                  data-testid="edit-org-preview"
                >
                  <div className="flex items-center justify-between px-3 py-2 bg-charcoal/5 border-b border-charcoal/10">
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-charcoal/60">Apercu du scan (cote decouvreur)</p>
                    <a
                      href={`/ping/${editingPingme}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] font-bold text-charcoal underline hover:no-underline"
                      data-testid="edit-org-preview-open"
                    >
                      Ouvrir la page reelle
                    </a>
                  </div>
                  {org.cover_image_url && (
                    <div className="aspect-[16/5] bg-charcoal/5 overflow-hidden">
                      <img src={resolvePhotoUrl(org.cover_image_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      {org.logo_url ? (
                        <img src={resolvePhotoUrl(org.logo_url)} alt="" className="w-9 h-9 rounded-lg border-2 border-charcoal object-cover bg-white" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg border-2 border-charcoal flex items-center justify-center" style={{ backgroundColor: primary }}>
                          <Building2 className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-bold text-sm truncate">{org.display_name || org.name}</p>
                        {org.tagline && <p className="text-[11px] italic text-charcoal/70 truncate">"{org.tagline}"</p>}
                      </div>
                    </div>
                    {org.message && <p className="text-xs text-charcoal/80 leading-snug">{org.message}</p>}
                    {Array.isArray(org.categories) && org.categories.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-charcoal/10">
                        <p className="text-[9px] uppercase font-bold tracking-wider text-charcoal/50 mb-1">Categories disponibles</p>
                        <div className="flex flex-wrap gap-1">
                          {org.categories.map((c) => (
                            <span key={c.key} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white border border-charcoal/30">{c.label}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="space-y-4">
              {/* Photo */}
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Photo</label>
                <div className="flex items-center gap-4">
                  <div className="relative group">
                    {editForm.photo_url ? (
                      <img
                        src={resolvePhotoUrl(editForm.photo_url)}
                        alt="Photo"
                        className="w-24 h-24 rounded-xl border-2 border-charcoal object-cover"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-xl border-2 border-dashed border-charcoal/30 bg-surface-alt flex items-center justify-center">
                        <Image className="w-8 h-8 text-charcoal/30" />
                      </div>
                    )}
                    <label className="absolute inset-0 flex items-center justify-center bg-charcoal/40 opacity-0 group-hover:opacity-100 rounded-xl cursor-pointer transition-opacity">
                      <Image className="w-6 h-6 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleEditPhotoUpload(e.target.files[0])}
                        data-testid="edit-photo-upload"
                      />
                    </label>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-charcoal/60">Survolez la photo pour la changer</p>
                    {editForm.photo_url && (
                      <button onClick={() => setEditForm({ ...editForm, photo_url: '' })} className="text-xs text-danger hover:underline mt-1">Supprimer la photo</button>
                    )}
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Nom *</label>
                <input
                  className="input-brutal w-full"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="edit-pingme-name"
                />
              </div>

              {/* Category - use org's custom categories if available */}
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Categorie</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {(() => {
                    const pm = pingmes.find((p) => p.pingme_id === editingPingme);
                    const orgCats = pm?.organization?.categories;
                    if (Array.isArray(orgCats) && orgCats.length > 0) {
                      return orgCats.map((c) => ({ value: c.key, label: c.label, icon: Building2 }));
                    }
                    return PINGME_CATEGORIES;
                  })().map(cat => {
                    const CatIcon = cat.icon;
                    return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, category: cat.value })}
                      className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl border-2 text-[10px] font-bold transition-all min-h-[68px] text-center leading-tight ${
                        editForm.category === cat.value ? 'border-primary bg-primary/10 text-charcoal' : 'border-charcoal/20 text-charcoal/60 hover:border-charcoal/40'
                      }`}
                    >
                      <CatIcon className="w-5 h-5 shrink-0" />
                      <span className="break-words">{cat.label}</span>
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Label Text — locked when pingme belongs to an organization */}
              {(() => {
                const pm = pingmes.find((p) => p.pingme_id === editingPingme);
                if (pm?.org_id) {
                  return (
                    <div className="rounded-xl border-2 border-dashed border-charcoal/20 bg-surface-alt/50 px-4 py-3" data-testid="edit-pingme-org-locked-label">
                      <p className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/60 mb-1">Texte de l'etiquette · Couleur · Sticker</p>
                      <p className="text-sm text-charcoal/70 leading-snug">
                        Geres par votre organisation <strong>{pm?.organization?.name || ''}</strong> pour assurer la coherence de marque.
                      </p>
                    </div>
                  );
                }
                return (
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Texte de l'etiquette</label>
                    <input
                      className="input-brutal w-full"
                      placeholder={t('dashboard.labelText')}
                      value={editForm.label_text}
                      onChange={(e) => setEditForm({ ...editForm, label_text: e.target.value })}
                      data-testid="edit-pingme-label-text"
                    />
                    <p className="text-xs text-charcoal/50 mt-1">Texte affiche sur la plaquette/sticker</p>
                  </div>
                );
              })()}

              {/* Preset Message - large textarea */}
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Message pre-enregistre</label>
                <p className="text-xs text-charcoal/60 mb-2">Ce message s'affichera lors du scan de votre PingMe par un tiers.</p>
                <textarea
                  className="input-brutal w-full min-h-[180px] resize-y"
                  placeholder={t('dashboard.presetMessage')}
                  value={editForm.preset_message}
                  onChange={(e) => setEditForm({ ...editForm, preset_message: e.target.value })}
                  data-testid="edit-pingme-preset-message"
                />
                {editForm.preset_message && (
                  <div className="mt-2">
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1 block">Mode d'affichage</label>
                    <select
                      className="input-brutal w-full"
                      value={editForm.preset_message_mode}
                      onChange={(e) => setEditForm({ ...editForm, preset_message_mode: e.target.value })}
                      data-testid="edit-pingme-preset-mode"
                    >
                      <option value="before">{t('dashboard.presetModeBefore')}</option>
                      <option value="only">Afficher seul (pas de conversation)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Color picker — hidden for org-managed pingmes (see locked banner above) */}
              {(() => {
                const pm = pingmes.find((p) => p.pingme_id === editingPingme);
                if (pm?.org_id) return null;
                return (
                  <ColorPicker
                    label="Couleur du sticker"
                    value={editForm.bg_color}
                    onChange={(c) => setEditForm({ ...editForm, bg_color: c })}
                    testid="edit-color-picker"
                  />
                );
              })()}

              {/* Notification Prefs per PingMe */}
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block flex items-center gap-1">
                  <Bell className="w-3.5 h-3.5" /> Notifications
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.notification_prefs === null}
                      onChange={() => setEditForm({ ...editForm, notification_prefs: editForm.notification_prefs === null ? { email: true, sms: false, push: false } : null })}
                      className="w-4 h-4 accent-primary"
                      data-testid="edit-notif-default"
                    />
                    Utiliser les preferences du compte
                  </label>
                  {editForm.notification_prefs !== null && (
                    <div className="ml-6 space-y-1.5">
                      {[
                        { key: 'email', icon: Mail, label: 'Email' },
                        { key: 'sms', icon: Smartphone, label: 'SMS' },
                      ].map((n) => (
                        <label key={n.key} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.notification_prefs?.[n.key] || false}
                            onChange={() => setEditForm({
                              ...editForm,
                              notification_prefs: { ...editForm.notification_prefs, [n.key]: !editForm.notification_prefs?.[n.key] }
                            })}
                            className="w-4 h-4 accent-primary"
                            data-testid={`edit-notif-${n.key}`}
                          />
                          <n.icon className="w-3.5 h-3.5 text-charcoal/60" />
                          {n.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-4 border-t border-charcoal/10">
              <button
                onClick={saveEdit}
                disabled={editSaving || !editForm.name.trim()}
                className="flex-1 btn-primary disabled:opacity-50"
                data-testid="save-edit-pingme-btn"
              >
                {editSaving ? 'Sauvegarde...' : 'Enregistrer'}
              </button>
              <button onClick={() => setEditingPingme(null)} className="btn-outline px-6">Annuler</button>
            </div>

            {/* Tagora ID seal */}
            {(editingPingme || '').startsWith('TGR-') && (
              <div className="mt-4 pt-4 border-t border-charcoal/10 flex flex-col items-center gap-2">
                <Link to={`/verify/${editingPingme}`} className="hover:opacity-80 transition-opacity" data-testid="edit-tagora-seal">
                  <TagoraSeal tagoraId={editingPingme} verified />
                </Link>
                <Link to={`/verify/${editingPingme}`} className="text-[11px] font-bold text-charcoal/60 hover:text-charcoal hover:underline">
                  Verifier ce code →
                </Link>
              </div>
            )}

            {/* Transfer this PingMe to another user */}
            <div className="mt-4 pt-4 border-t border-charcoal/10 bg-accent/10 rounded-xl p-3">
              <p className="text-xs font-bold text-charcoal mb-2 uppercase tracking-wider flex items-center gap-1">
                <ArrowRightLeft className="w-3.5 h-3.5" /> Transferer ce PingMe
              </p>
              <p className="text-xs text-charcoal/70 mb-2">Generez un lien de transfert a usage unique pour donner ce PingMe a une autre personne.</p>
              <button
                onClick={() => {
                  const pm = pingmes.find((p) => p.pingme_id === editingPingme);
                  if (pm) generateTransfer(pm);
                }}
                className="w-full bg-charcoal text-white border-2 border-charcoal rounded-full px-4 py-2 text-sm font-bold hover:bg-charcoal/90 flex items-center justify-center gap-2"
                data-testid="transfer-edit-btn"
              >
                <ArrowRightLeft className="w-4 h-4" /> Generer un lien de transfert
              </button>
            </div>

            {/* Archive PingMe (soft-delete, recoverable for 30 days) */}
            <div className="mt-4 pt-4 border-t border-danger/20 bg-danger/5 rounded-xl p-3">
              <p className="text-xs font-bold text-danger mb-2 uppercase tracking-wider">Archiver ce PingMe</p>
              <p className="text-xs text-charcoal/70 mb-2">L'objet n'existe plus ? Archivez ce PingMe. Il sera recuperable pendant 30 jours depuis l'archive.</p>
              <button
                onClick={() => {
                  const id = editingPingme;
                  setEditingPingme(null);
                  deletePingme(id);
                }}
                className="w-full bg-danger text-white border-2 border-charcoal rounded-full px-4 py-2 text-sm font-bold hover:bg-danger/90 flex items-center justify-center gap-2"
                data-testid="delete-pingme-btn"
              >
                <Trash2 className="w-4 h-4" /> Archiver ce PingMe (l'objet n'existe plus)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/80 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setZoomedImage(null)}
          data-testid="image-zoom-modal"
        >
          <button onClick={() => setZoomedImage(null)} className="absolute top-4 right-4 p-2 bg-white rounded-full border-2 border-charcoal shadow-brutal z-10">
            <X className="w-6 h-6 text-charcoal" />
          </button>
          <img
            src={zoomedImage}
            alt="Zoom"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-2xl border-4 border-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Transfer QR Dialog */}
      {transferData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/50 backdrop-blur-sm px-4" onClick={() => setTransferData(null)}>
          <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="transfer-dialog">
            <ArrowRightLeft className="w-10 h-10 text-primary mx-auto mb-3" />
            <h3 className="text-xl font-bold font-heading text-charcoal mb-2">Transferer "{transferData.pingme_name}"</h3>
            <p className="text-sm text-charcoal/60 mb-4">Montrez ce QR code au nouveau proprietaire. Valable 1 heure.</p>
            <div className="bg-white border-2 border-charcoal rounded-xl p-4 mb-4 inline-block">
              <QRCodeSVG value={transferData.transferUrl} size={180} level="L" includeMargin={false} />
            </div>
            <p className="text-xs text-charcoal/40 mb-4 break-all">{transferData.transferUrl}</p>
            <button
              onClick={() => { navigator.clipboard.writeText(transferData.transferUrl); toast.success('Lien copie !'); }}
              className="btn-outline w-full text-sm mb-2"
              data-testid="transfer-copy-link"
            >
              Copier le lien
            </button>
            <button onClick={() => setTransferData(null)} className="w-full text-sm text-charcoal/50 hover:text-charcoal py-2">
              {t('actions.close')}
            </button>
          </div>
        </div>
      )}
      {/* QR Code zoom modal */}
      {qrZoomFor && (
        <div className="fixed inset-0 bg-charcoal/80 z-50 flex items-center justify-center p-4" onClick={() => setQrZoomFor(null)} data-testid="qr-zoom-modal">
          <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-lg max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setQrZoomFor(null)} className="absolute top-4 right-4 p-2 hover:bg-surface-alt rounded-full" data-testid="qr-zoom-close">
              <X className="w-5 h-5" />
            </button>
            <p className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-2">QR Code</p>
            <h3 className="font-heading font-bold text-lg text-charcoal mb-4 truncate">{qrZoomFor.name}</h3>
            {(() => {
              const bg = qrZoomFor.bg_color || '#FFFFFF';
              const h = bg.replace('#', '');
              const r = parseInt(h.substring(0, 2) || '0', 16) / 255;
              const g = parseInt(h.substring(2, 4) || '0', 16) / 255;
              const b = parseInt(h.substring(4, 6) || '0', 16) / 255;
              const ch = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
              const lum = 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
              const fg = lum < 0.45 ? '#FFFFFF' : '#0f172a';
              return (
                <div className="rounded-lg p-4 border-2 border-charcoal inline-block" style={{ backgroundColor: bg }}>
                  <QRCodeSVG value={qrZoomFor.qr_code_url} size={240} level="M" includeMargin={false} bgColor={bg} fgColor={fg} />
                </div>
              );
            })()}
            {(qrZoomFor.pingme_id || '').startsWith('TGR-') && (
              <p className="font-mono text-xs text-charcoal/60 mt-3 break-all">{qrZoomFor.pingme_id}</p>
            )}
            <div className="mt-5 flex gap-2 justify-center">
              {!qrZoomFor.org_id && (
                <button
                  onClick={() => { setStickerPickerFor(qrZoomFor); setQrZoomFor(null); }}
                  className="btn-primary text-sm inline-flex items-center gap-2"
                  data-testid="qr-zoom-download-sticker"
                >
                  <Download className="w-4 h-4" /> Telecharger un sticker
                </button>
              )}
              <button onClick={() => setQrZoomFor(null)} className="btn-outline text-sm" data-testid="qr-zoom-done">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      <ArchiveModal
        open={showArchive}
        onClose={() => setShowArchive(false)}
        onRestored={() => { setShowArchive(false); fetchPingmes(); }}
      />

      {/* Sticker template picker */}
      <StickerPickerModal
        open={!!stickerPickerFor}
        pingmeId={stickerPickerFor?.pingme_id}
        pingmeName={stickerPickerFor?.name}
        onClose={() => setStickerPickerFor(null)}
      />
    </div>
  );
};

export default Dashboard;
